import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Modal, ActionBtn } from './Shared';
import { rotaAPI } from '../services/api';

/* ------------------------------------------------------------------
 * @admin-clear-day-v1 (30 Sep 2026) -- M-40
 *
 * Clearing a doctor's day used to be done one appointment at a time.
 * This is the admin face of the two backend endpoints.
 *
 * The preview changes nothing. The execute call carries the count the
 * preview reported back to the server, so a patient who books in the
 * meantime aborts the run (409 STALE_PREVIEW) instead of being
 * cancelled without anyone having seen them -- when that happens we
 * simply re-preview and show the new numbers.
 *
 * Nothing here decides refunds. The server does, through the same
 * cancellation path a single manual cancellation uses.
 * ------------------------------------------------------------------ */

const boxStyle = {
    padding: '10px 12px',
    background: 'var(--bg)',
    borderRadius: 8,
    marginBottom: 12,
    fontSize: 14,
};

const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
};

export default function ClearDayModal({ open, onClose, doctorId, doctorName, date, onDone }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [reason, setReason] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const loadPreview = useCallback(async () => {
        if (!doctorId || !date) return;
        setLoading(true);
        setError('');
        try {
            const res = await rotaAPI.clearDayPreview(doctorId, date);
            setPreview(res.data.data);
        } catch (e) {
            setPreview(null);
            setError(e.response?.data?.error || 'Η προεπισκόπηση απέτυχε.');
        } finally {
            setLoading(false);
        }
    }, [doctorId, date]);

    useEffect(() => {
        if (!open) return;
        setResult(null);
        setReason('');
        loadPreview();
    }, [open, loadPreview]);

    const handleRun = async () => {
        if (!preview || !reason.trim()) return;
        setRunning(true);
        try {
            const res = await rotaAPI.clearDay(doctorId, {
                date,
                reason: reason.trim(),
                confirm: true,
                expectedAppointments: preview.expectedAppointments,
            });
            setResult(res.data.data);
            toast.success('Η ημέρα καθαρίστηκε.');
        } catch (e) {
            if (e.response?.status === 409) {
                toast.error('Η ημέρα άλλαξε από την προεπισκόπηση. Ελέγξτε τα νέα νούμερα.');
                await loadPreview();
            } else {
                toast.error(e.response?.data?.error || 'Ο καθαρισμός απέτυχε.');
            }
        } finally {
            setRunning(false);
        }
    };

    const handleClose = () => {
        if (result && onDone) onDone();
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose} title="Καθαρισμός ημέρας" size="md">
            <div style={{ padding: 20 }}>
                <div style={boxStyle}>
                    <div style={rowStyle}>
                        <span style={{ color: 'var(--muted)' }}>Ιατρός</span>
                        <strong>{preview?.doctor || doctorName || '—'}</strong>
                    </div>
                    <div style={rowStyle}>
                        <span style={{ color: 'var(--muted)' }}>Ημερομηνία</span>
                        <strong>{date}</strong>
                    </div>
                </div>

                {loading && <p style={{ fontSize: 14, color: 'var(--muted)' }}>Υπολογισμός…</p>}

                {error && !loading && (
                    <p style={{ fontSize: 14, color: 'var(--crimson)' }}>{error}</p>
                )}

                {!result && preview && !loading && (
                    <>
                        <div style={boxStyle}>
                            <div style={rowStyle}>
                                <span>Ραντεβού που θα ακυρωθούν</span>
                                <strong>{preview.willCancel}</strong>
                            </div>
                            <div style={rowStyle}>
                                <span>Ποσό προς επιστροφή</span>
                                <strong>{preview.willRefundEuros}&nbsp;€</strong>
                            </div>
                            <div style={rowStyle}>
                                <span>Ραντεβού που θα παραμείνουν</span>
                                <strong>{preview.willSkip}</strong>
                            </div>
                            <div style={rowStyle}>
                                <span>Συνεδρίες που θα κλείσουν</span>
                                <strong>{preview.sessionsToClose}</strong>
                            </div>
                        </div>

                        {preview.willCancel === 0 && preview.sessionsToClose === 0 && (
                            <p style={{ fontSize: 14, color: 'var(--muted)' }}>
                                Δεν υπάρχει τίποτα να καθαριστεί αυτή την ημέρα.
                            </p>
                        )}

                        <div style={{ marginBottom: 12 }}>
                            <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
                                Αιτία ακύρωσης (υποχρεωτικό)
                            </label>
                            <textarea
                                rows={2}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Π.χ. ασθένεια ιατρού"
                                style={{ width: '100%', padding: 8, fontSize: 14, fontFamily: 'inherit' }}
                            />
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                                Στέλνεται στους ασθενείς μαζί με την ακύρωση.
                            </span>
                        </div>

                        <div className="modalFooter" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <ActionBtn variant="default" onClick={handleClose}>Άκυρο</ActionBtn>
                            <ActionBtn
                                variant="primary"
                                onClick={handleRun}
                                disabled={running || !reason.trim() || (preview.willCancel === 0 && preview.sessionsToClose === 0)}
                            >
                                {running
                                    ? 'Εκτελείται…'
                                    : 'Επιβεβαίωση ακύρωσης ' + preview.willCancel + ' ραντεβού'}
                            </ActionBtn>
                        </div>
                    </>
                )}

                {result && (
                    <>
                        <div style={boxStyle}>
                            <div style={rowStyle}>
                                <span>Ακυρώθηκαν</span>
                                <strong>{result.cancelled}</strong>
                            </div>
                            <div style={rowStyle}>
                                <span>Επιστράφηκαν</span>
                                <strong>{result.refundedEuros}&nbsp;€</strong>
                            </div>
                            <div style={rowStyle}>
                                <span>Παραλείφθηκαν</span>
                                <strong>{result.skipped}</strong>
                            </div>
                            <div style={rowStyle}>
                                <span>Συνεδρίες που έκλεισαν</span>
                                <strong>{result.sessionsClosed}</strong>
                            </div>
                        </div>

                        {result.needsManualRefund?.length > 0 && (
                            <div style={{ ...boxStyle, background: '#FDECEC' }}>
                                <strong>Χρειάζονται χειροκίνητη επιστροφή ({result.needsManualRefund.length})</strong>
                                <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 13 }}>
                                    {result.needsManualRefund.map((m) => (
                                        <li key={m.id}>{m.id} — {m.reason || 'άγνωστη αιτία'}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {result.failed > 0 && (
                            <div style={{ ...boxStyle, background: '#FDECEC' }}>
                                <strong>Απέτυχαν ({result.failed})</strong>
                                <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 13 }}>
                                    {(result.failedDetails || []).map((f) => (
                                        <li key={f.id}>{f.id} — {f.error}</li>
                                    ))}
                                </ul>
                                <p style={{ fontSize: 13, margin: '6px 0 0' }}>
                                    Οι {result.sessionsLeftOpen} συνεδρίες παρέμειναν ανοιχτές επίτηδες, ώστε
                                    η ημέρα να μένει ορατή. Ξανατρέξτε τον καθαρισμό αφού λυθεί το πρόβλημα.
                                </p>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <ActionBtn variant="primary" onClick={handleClose}>Κλείσιμο</ActionBtn>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
